import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroNewsTicker from './HeroNewsTicker';

interface HeroSectionProps {
  onSignClick?: () => void;
  signatureCount?: number;
  petitionCount?: number;
  expirDt?: string;
}

function useAnimatedCount(target: number, duration = 1800, delay = 300) {
  const [count, setCount] = useState(0);
  const prevTarget = useRef(0);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    if (target === prevTarget.current && count === target) return;
    const startVal = prevTarget.current;
    prevTarget.current = target;
    const startTime = performance.now() + delay;
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const animate = (now: number) => {
      if (now < startTime) {
        frameRef.current = requestAnimationFrame(animate);
        return;
      }
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const current = Math.round(startVal + (target - startVal) * easedProgress);
      setCount(current);
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration, delay, count]);

  return count;
}

export default function HeroSection({ signatureCount = 0, petitionCount = 0, expirDt = '' }: HeroSectionProps) {
  const navigate = useNavigate();

  const PETITION_TARGET = 50000;
  const SIGNATURE_TARGET = 10000000;

  const animatedPetition = useAnimatedCount(petitionCount, 2000, 400);
  const animatedSignature = useAnimatedCount(signatureCount, 2200, 600);

  const petitionPct = Math.min((petitionCount / PETITION_TARGET) * 100, 100);
  const signaturePct = Math.min((signatureCount / SIGNATURE_TARGET) * 100, 100);
  const animatedPetitionPct = Math.min((animatedPetition / PETITION_TARGET) * 100, 100);
  const animatedSignaturePct = Math.min((animatedSignature / SIGNATURE_TARGET) * 100, 100);

  const totalCount = petitionCount + signatureCount;
  const animatedTotal = useAnimatedCount(totalCount, 2400, 800);

  const formatExpirDt = (dt: string) => {
    if (!dt) return '';
    // YYYYMMDD → YYYY.MM.DD
    if (dt.length === 8) return `${dt.slice(0, 4)}.${dt.slice(4, 6)}.${dt.slice(6, 8)}`;
    return dt;
  };

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      <div className="relative flex-1 flex items-center justify-center pt-24 md:pt-8">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/6f810c6a8ddd33550ce0390c7ddc413b.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <HeroNewsTicker />

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight" style={{ fontFamily: "'Nanum Myeongjo', serif" }}>
            사법정의를 위한<br />청년 120인 선언
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-red-200 mb-3 md:mb-4 font-semibold px-2">
            &quot;사법장악 독재 3법을 즉각 철회하라!&quot;
          </p>
          <p className="text-sm sm:text-base md:text-lg text-gray-200 mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed px-2">
            삼권분립을 훼손하고 대한민국의 헌정 질서를 위협하는<br className="hidden sm:block" />
            <strong className="text-white">사법장악 3법</strong>의 즉각적인 철회를 강력히 촉구합니다
          </p>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-8 md:mb-10 px-2">
            <a
              href="https://petitions.assembly.go.kr/proceed/registered/48DF3A32E4373F31E064ECE7A7064E8B"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-white text-red-600 px-6 md:px-10 py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:bg-gray-100 transition-all shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-government-line mr-2"></i>
              국회 국민동의청원
            </a>
            <button
              onClick={() => navigate('/sign')}
              className="w-full sm:w-auto bg-red-600 text-white px-6 md:px-10 py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:bg-red-700 transition-all shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-quill-pen-line mr-2"></i>
              범국민 서명운동
            </button>
          </div>

          {/* A/B 카운터 카드 */}
          <div className="max-w-3xl mx-auto px-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">

              {/* A. 국회 국민동의 청원 */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 md:p-5 shadow-2xl text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-red-100 rounded-lg shrink-0">
                    <i className="ri-government-line text-sm text-red-600"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-gray-800 leading-tight">국회 국민동의 청원</p>
                    <p className="text-[10px] text-gray-400">5만 명 목표</p>
                  </div>
                </div>
                <div className="flex items-end justify-between mb-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl md:text-3xl font-black text-red-600 tabular-nums leading-none">
                      {animatedPetition.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-400 font-medium">/ {PETITION_TARGET.toLocaleString()}</span>
                  </div>
                  <span className="text-xs font-bold text-red-500">{petitionPct.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden mb-2">
                  <div
                    className="bg-gradient-to-r from-red-500 to-rose-500 h-2.5 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${Math.max(animatedPetitionPct, 0.5)}%` }}
                  ></div>
                </div>
                {expirDt && (
                  <p className="text-[10px] text-gray-400 flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    마감: {formatExpirDt(expirDt)}
                  </p>
                )}
                {!expirDt && (
                  <p className="text-[10px] text-gray-400 flex items-center gap-1">
                    <i className="ri-external-link-line"></i>
                    국회 청원 사이트에서 참여
                  </p>
                )}
              </div>

              {/* B. 범국민 서명운동 */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 md:p-5 shadow-2xl text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-lg shrink-0">
                    <i className="ri-quill-pen-line text-sm text-teal-600"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-gray-800 leading-tight">범국민 서명운동</p>
                    <p className="text-[10px] text-gray-400">1,000만 명 목표</p>
                  </div>
                </div>
                <div className="flex items-end justify-between mb-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl md:text-3xl font-black text-teal-600 tabular-nums leading-none">
                      {animatedSignature.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-400 font-medium">/ {SIGNATURE_TARGET.toLocaleString()}</span>
                  </div>
                  <span className="text-xs font-bold text-teal-500">{signaturePct.toFixed(2)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden mb-2">
                  <div
                    className="bg-gradient-to-r from-teal-500 to-cyan-500 h-2.5 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${Math.max(animatedSignaturePct, 0.5)}%` }}
                  ></div>
                </div>
                <button
                  onClick={() => navigate('/sign')}
                  className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-right-line text-xs"></i>
                  서명 참여하기
                </button>
              </div>
            </div>

            {/* 통합 카운터 */}
            <div className="bg-gradient-to-r from-red-600/90 to-rose-700/90 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-2xl flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
                  <i className="ri-fire-line text-lg text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-red-100 font-medium">청원 + 서명 통합</p>
                  <p className="text-[10px] text-red-200">사법정의를 위해 함께하는 목소리</p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="flex items-baseline gap-1 justify-end">
                  <span className="text-2xl md:text-3xl font-black text-white tabular-nums leading-none">
                    {animatedTotal.toLocaleString()}
                  </span>
                  <span className="text-sm text-red-200 font-bold">명</span>
                </div>
                <p className="text-[10px] text-red-200 mt-0.5">총 참여자</p>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl md:text-4xl text-white opacity-70"></i>
        </div>
      </div>
    </section>
  );
}
